import { useEffect, useState } from "react";
import Header from "@/reusable_sections/Header";
import Footer from "@/reusable_sections/Footer";
const SHOW_WOOCOMMERCE_DEBUG = false;
type StoreApiPrices = {
  price?: string;
  regular_price?: string;
  sale_price?: string;
  currency_code?: string;
  currency_symbol?: string;
  currency_minor_unit?: number;
  currency_decimal_separator?: string;
  currency_thousand_separator?: string;
  currency_prefix?: string;
  currency_suffix?: string;
};
type StoreApiImage = {
  id?: number;
  src?: string;
  thumbnail?: string;
  srcset?: string;
  sizes?: string;
  name?: string;
  alt?: string;
};
type StoreApiCategory = {
  id?: number;
  name?: string;
  slug?: string;
};
type StoreApiProduct = {
  id?: number;
  name?: string;
  slug?: string;
  permalink?: string;
  short_description?: string;
  description?: string;
  categories?: StoreApiCategory[];
  prices?: StoreApiPrices;
  images?: StoreApiImage[];
};
type GuideProduct = {
  slug: string;
  badge: string;
  coverBadge: string;
  title: string;
  shortTitle: string;
  description: string;
  features: string[];
  theme: "coral" | "teal" | "sage" | "plum";
  anchor: string;
  url: string;
  footerLabel: string;
};
const GUIDE_PRODUCTS: GuideProduct[] = [{
  slug: "funded-ndis-guide-pda",
  badge: "Priority guide",
  coverBadge: "NDIS Guide",
  title: "FUNDED — The Complete NDIS Guide for PDA Families",
  shortTitle: "FUNDED",
  description: "For families preparing, protecting or rebuilding NDIS funding for a child with a PDA profile. Templates, evidence language and planning support included.",
  features: ["NDIS evidence preparation", "Functional impact language", "Templates, letters and review support"],
  theme: "coral",
  anchor: "ndis",
  url: "/product/funded-ndis-guide-pda/",
  footerLabel: "Digital guide"
}, {
  slug: "complete-pda-bundle",
  badge: "Best value",
  coverBadge: "Bundle",
  title: "The Complete PDA Bundle",
  shortTitle: "Complete PDA Bundle",
  description: "For families who want the full practical library — home, school, NDIS and regulation support across one instant-download collection.",
  features: ["All core guides in one download", "Save on individual guide prices", "Home, school and NDIS support"],
  theme: "teal",
  anchor: "bundle",
  url: "/product/complete-pda-bundle/",
  footerLabel: "Bundle"
}, {
  slug: "pda-teacher-handbook",
  badge: "School resource",
  coverBadge: "School resource",
  title: "The PDA Teacher Handbook",
  shortTitle: "Teacher Handbook",
  description: "Classroom strategies that reduce escalation, support trust-based engagement and work within Australian school systems.",
  features: ["Demand-aware strategies", "Practical language and adjustments", "Printable school tools", "Single or whole-school licence"],
  theme: "teal",
  anchor: "school",
  url: "/product/pda-teacher-handbook/",
  footerLabel: "Digital guide"
}, {
  slug: "australian-parents-compass-pda",
  badge: "Parent guide",
  coverBadge: "Parent guide",
  title: "The Australian Parent’s Compass",
  shortTitle: "Parent’s Compass",
  description: "Plain-English PDA support across home life, school pressure and the daily overwhelm of raising a PDA child in Australia.",
  features: ["Home and school strategies", "Scripts and worksheets", "Australian schooling context"],
  theme: "sage",
  anchor: "parents",
  url: "/product/australian-parents-compass-pda/",
  footerLabel: "Digital guide"
}, {
  slug: "the-rsd-compass",
  badge: "RSD guide",
  coverBadge: "RSD guide",
  title: "The RSD Compass",
  shortTitle: "The RSD Compass",
  description: "A practical guide for understanding rejection sensitive dysphoria — what it is, how it shows up in PDA children, and what actually helps.",
  features: ["What RSD looks like", "Home and school responses", "Practical tools"],
  theme: "plum",
  anchor: "rsd",
  url: "/product/the-rsd-compass/",
  footerLabel: "Digital guide"
}, {
  slug: "pda-girls-women-recognition-guide",
  badge: "Girls & women",
  coverBadge: "Recognition guide",
  title: "PDA Girls & Women Recognition Guide",
  shortTitle: "Girls & Women Guide",
  description: "A practical recognition guide for understanding how PDA can present in girls and women, including masking, internalised distress and missed support needs.",
  features: ["Girls and women presentation", "Masking and internalised distress", "Recognition and support prompts"],
  theme: "sage",
  anchor: "girls-women",
  url: "/product/pda-girls-women-recognition-guide/",
  footerLabel: "Digital guide"
}, {
  slug: "ndis-pda-guide-australian-families",
  badge: "NDIS starter",
  coverBadge: "NDIS starter",
  title: "The NDIS & PDA Guide for Australian Families",
  shortTitle: "NDIS & PDA Guide",
  description: "Plain-language support for families starting with NDIS access, funding, evidence, reports and PDA-specific support needs.",
  features: ["NDIS basics", "Evidence and reports", "PDA-specific support needs"],
  theme: "sage",
  anchor: "ndis-starter",
  url: "/product/ndis-pda-guide-australian-families/",
  footerLabel: "Digital guide"
}, {
  slug: "teen-pda-guide",
  badge: "Teen support",
  coverBadge: "Teen guide",
  title: "Teen PDA Guide for Parents and Educators",
  shortTitle: "Teen PDA Guide",
  description: "Support for the teenage years with PDA — school pressure, autonomy needs, shutdowns, burnout and family conflict.",
  features: ["Teen-specific PDA support", "School and home context", "Parent and educator guidance"],
  theme: "coral",
  anchor: "teens",
  url: "/product/teen-pda-guide/",
  footerLabel: "Digital guide"
}, {
  slug: "pda-teen-rsd-support-pack",
  badge: "Teen + RSD bundle",
  coverBadge: "Bundle",
  title: "PDA Teen + RSD Support Pack",
  shortTitle: "Teen + RSD Support Pack",
  description: "Two practical Australian guides for PDA adolescence, school pressure, shame, rejection spirals and relationship repair.",
  features: ["348+ pages across two guides", "37 practical tools", "PDA and RSD support for the teen years"],
  theme: "plum",
  anchor: "teen-rsd-bundle",
  url: "/product/pda-teen-rsd-support-pack/",
  footerLabel: "Bundle"
}, {
  slug: "complete-library-funded",
  badge: "Complete support",
  coverBadge: "Bundle",
  title: "Complete Library + FUNDED",
  shortTitle: "Library + FUNDED",
  description: "The complete six-guide PDA Australia library plus the comprehensive FUNDED NDIS system, templates and planning tools.",
  features: ["Complete PDA guide library", "FUNDED NDIS guide and toolkit", "Instant digital access"],
  theme: "teal",
  anchor: "complete-library-funded",
  url: "/product/complete-library-funded/",
  footerLabel: "Bundle"
}, {
  slug: "home-school-alignment-pack",
  badge: "Home + school",
  coverBadge: "Bundle",
  title: "Home–School Alignment Pack",
  shortTitle: "Home–School Alignment",
  description: "Help parents and educators support the same child with one practical, low-demand approach across home and school.",
  features: ["Parent and teacher guidance", "Shared low-demand language", "Practical Australian context"],
  theme: "sage",
  anchor: "home-school-alignment",
  url: "/product/home-school-alignment-pack/",
  footerLabel: "Bundle"
}, {
  slug: "pda-regulation-workbook",
  badge: "Regulation toolkit",
  coverBadge: "Workbook",
  title: "The PDA Regulation Workbook",
  shortTitle: "PDA Regulation Workbook",
  description: "An interactive workbook of regulation tools, feeling charts, co-regulation scripts and sensory checklists for PDA children and their adults.",
  features: ["Practical regulation tools", "Co-regulation scripts", "Printable charts and checklists"],
  theme: "coral",
  anchor: "regulation-workbook",
  url: "/product/pda-regulation-workbook/",
  footerLabel: "Digital guide"
}];
const FEATURED_SLUGS = ["funded-ndis-guide-pda", "complete-pda-bundle"];
function formatStorePrice(prices?: StoreApiPrices) {
  if (!prices?.price) return null;
  const minorUnit = typeof prices.currency_minor_unit === "number" ? prices.currency_minor_unit : 2;
  const symbol = prices.currency_symbol || "$";
  const decimalSeparator = prices.currency_decimal_separator || ".";
  const thousandSeparator = prices.currency_thousand_separator || ",";
  const prefix = typeof prices.currency_prefix === "string" && prices.currency_prefix.length ? prices.currency_prefix : symbol;
  const suffix = prices.currency_suffix ?? "";
  const formatAmount = (raw?: string) => {
    if (!raw) return "";
    const numeric = Number(raw);
    if (!Number.isFinite(numeric)) return "";
    const amount = numeric / Math.pow(10, minorUnit);
    const fixed = amount.toFixed(minorUnit);
    const [whole, decimal] = fixed.split(".");
    const wholeWithThousands = whole.replace(/\B(?=(\d{3})+(?!\d))/g, thousandSeparator);
    const hasUsefulDecimals = minorUnit > 0 && decimal && Number(decimal) !== 0;
    return `${prefix}${wholeWithThousands}${hasUsefulDecimals ? `${decimalSeparator}${decimal}` : ""}${suffix}`;
  };
  const current = formatAmount(prices.price);
  const regular = formatAmount(prices.regular_price);
  const hasSale = prices.regular_price && prices.price && prices.regular_price !== prices.price && Number(prices.regular_price) > Number(prices.price);
  return {
    current,
    regular: hasSale ? regular : ""
  };
}
function getProductImage(product?: StoreApiProduct) {
  const image = product?.images?.[0];
  if (!image) return null;
  const src = image.src || image.thumbnail;
  if (!src) return null;
  return {
    src,
    srcset: image.srcset,
    sizes: image.sizes,
    alt: image.alt || image.name || product?.name || "PDA Australia guide cover"
  };
}
function stripProductHtml(value?: string) {
  if (!value) return "";
  const textarea = document.createElement("textarea");
  textarea.innerHTML = value.replace(/<[^>]*>/g, " ");
  return textarea.value.replace(/\s+/g, " ").trim();
}
function themeForProduct(product: StoreApiProduct): GuideProduct["theme"] {
  const searchableText = [product.slug, product.name, ...(product.categories?.map(category => category.name) || [])].filter(Boolean).join(" ").toLowerCase();
  if (/complete-library|home-school|bundle|support pack/.test(searchableText)) {
    return "teal";
  }
  if (/rsd|rejection|emotional/.test(searchableText)) return "plum";
  if (/teacher|educator|school/.test(searchableText)) return "teal";
  if (/funded|ndis/.test(searchableText)) return "coral";
  return "sage";
}
function storeProductToGuide(product: StoreApiProduct): GuideProduct {
  const slug = product.slug || `product-${product.id || "guide"}`;
  const title = product.name || "PDA Australia Guide";
  const categoryName = product.categories?.[0]?.name;
  const isBundle = /bundle|pack|library/.test(`${slug} ${title}`.toLowerCase());
  return {
    slug,
    badge: isBundle ? "Bundle" : categoryName || "Digital guide",
    coverBadge: isBundle ? "Bundle" : categoryName || "PDA guide",
    title,
    shortTitle: title,
    description: stripProductHtml(product.short_description || product.description) || "Practical Australian PDA guidance with instant digital access.",
    features: ["Instant digital access", "Australian context", "Practical, neuro-affirming guidance"],
    theme: themeForProduct(product),
    anchor: `product-${slug}`,
    url: product.permalink || `/product/${slug}/`,
    footerLabel: isBundle ? "Bundle" : "Digital guide"
  };
}
function ProductPrice({
  product,
  hasLoaded
}: {
  product?: StoreApiProduct;
  hasLoaded: boolean;
}) {
  if (!hasLoaded) {
    return <span className="pdaa-shop-price-unavailable">Loading…</span>;
  }
  const formatted = formatStorePrice(product?.prices);
  if (!formatted?.current) {
    return <span className="pdaa-shop-price-unavailable">View price</span>;
  }
  return <>
      {formatted.regular ? <del>{formatted.regular}</del> : null}
      <ins>{formatted.current}</ins>
    </>;
}
function ProductCover({
  guide,
  product,
  productUrl
}: {
  guide: GuideProduct;
  product?: StoreApiProduct;
  productUrl: string;
}) {
  const image = getProductImage(product);
  if (image) {
    return <a className="pdaa-shop-cover pdaa-shop-cover-image" href={productUrl} aria-label={`View ${guide.title}`}>
        <span className="pdaa-cover-badge">{guide.coverBadge}</span>

        <div className="pdaa-dynamic-image-wrap">
          <img src={image.src} srcSet={image.srcset} sizes={image.sizes} alt={image.alt} loading="lazy" />
        </div>

        <span className="pdaa-cover-footer">PDA Australia</span>
      </a>;
  }
  return <a className="pdaa-shop-cover" href={productUrl} aria-label={`View ${guide.title}`}>
      <span className="pdaa-cover-badge">{guide.coverBadge}</span>
      <span className="pdaa-cover-short">{guide.shortTitle}</span>
      <span className="pdaa-cover-footer">PDA Australia</span>
    </a>;
}
function ProductCard({
  guide,
  product,
  hasLoaded,
  featured = false
}: {
  guide: GuideProduct;
  product?: StoreApiProduct;
  hasLoaded: boolean;
  featured?: boolean;
}) {
  const productUrl = product?.permalink || guide.url;
  return <article className={`pdaa-shop-product theme-${guide.theme} ${featured ? "pdaa-featured-product" : ""}`} id={featured ? `featured-${guide.anchor}` : guide.anchor}>
      <ProductCover guide={guide} product={product} productUrl={productUrl} />

      <div className="pdaa-shop-product-body">
        <div className="pdaa-product-meta">
          <span className="pdaa-product-badge">{guide.badge}</span>
          <span className="pdaa-product-price">
            <ProductPrice product={product} hasLoaded={hasLoaded} />
          </span>
        </div>

        <h3>{guide.title}</h3>
        <p>{guide.description}</p>

        <ul className="pdaa-product-features">
          {guide.features.map(feature => <li key={feature}>{feature}</li>)}
        </ul>

        <div className="pdaa-product-footer">
          <span>{guide.footerLabel}</span>
          <a href={productUrl}>View guide</a>
        </div>
      </div>
    </article>;
}
function PremiumShopContent() {
  const [products, setProducts] = useState<Record<string, StoreApiProduct>>({});
  const [hasLoaded, setHasLoaded] = useState(false);
  const [debugMessage, setDebugMessage] = useState("Waiting for WooCommerce data…");
  useEffect(() => {
    let cancelled = false;
    async function fetchProductsFromUrl(apiUrl: string) {
      const response = await fetch(apiUrl, {
        method: "GET",
        credentials: "omit",
        cache: "no-store",
        headers: {
          Accept: "application/json"
        }
      });
      if (!response.ok) {
        throw new Error(`WooCommerce API failed: ${response.status}`);
      }
      const data = await response.json();
      return Array.isArray(data) ? data as StoreApiProduct[] : [];
    }
    async function loadProducts() {
      const possibleUrls = ["/wp-json/wc/store/v1/products?per_page=100", "https://pdaaustralia.com.au/wp-json/wc/store/v1/products?per_page=100"];
      let productsArray: StoreApiProduct[] = [];
      let successfulUrl = "";
      for (const apiUrl of possibleUrls) {
        try {
          productsArray = await fetchProductsFromUrl(apiUrl);
          successfulUrl = apiUrl;
          break;
        } catch {
          // Try the next URL.
        }
      }
      const nextProducts: Record<string, StoreApiProduct> = {};
      productsArray.forEach(product => {
        if (product.slug) {
          nextProducts[product.slug] = product;
        }
      });
      const missingSlugs = GUIDE_PRODUCTS.filter(guide => !nextProducts[guide.slug]).map(guide => guide.slug);
      if (missingSlugs.length > 0) {
        await Promise.all(missingSlugs.map(async slug => {
          const slugUrls = [`/wp-json/wc/store/v1/products?slug=${encodeURIComponent(slug)}`, `https://pdaaustralia.com.au/wp-json/wc/store/v1/products?slug=${encodeURIComponent(slug)}`];
          for (const apiUrl of slugUrls) {
            try {
              const data = await fetchProductsFromUrl(apiUrl);
              if (data[0]) {
                nextProducts[slug] = data[0];
                break;
              }
            } catch {
              // Try next URL.
            }
          }
        }));
      }
      if (!cancelled) {
        const loadedCount = Object.keys(nextProducts).length;
        setProducts(nextProducts);
        setHasLoaded(true);
        if (loadedCount > 0) {
          setDebugMessage(`WooCommerce products loaded: ${loadedCount} / ${GUIDE_PRODUCTS.length}${successfulUrl ? ` via ${successfulUrl}` : ""}`);
        } else {
          setDebugMessage(`WooCommerce products loaded: 0 / ${GUIDE_PRODUCTS.length}. API data was not available inside this preview.`);
        }
      }
    }
    loadProducts().catch(() => {
      if (!cancelled) {
        setProducts({});
        setHasLoaded(true);
        setDebugMessage(`WooCommerce products loaded: 0 / ${GUIDE_PRODUCTS.length}. Fetch failed inside this preview.`);
      }
    });
    return () => {
      cancelled = true;
    };
  }, []);
  const curatedSlugs = new Set(GUIDE_PRODUCTS.map(guide => guide.slug));
  const automaticGuides = Object.values(products).filter(product => product.slug && !curatedSlugs.has(product.slug)).map(storeProductToGuide);
  const allGuides = [...GUIDE_PRODUCTS, ...automaticGuides];
  const featuredGuides = allGuides.filter(guide => FEATURED_SLUGS.includes(guide.slug));
  const loadedProductCount = Object.keys(products).length;
  return <main className="pdaa-shop-page">
      <style>{`
        .pdaa-shop-page {
          --cream: #f6f1e9;
          --cream2: #fbf8f2;
          --paper: #fffdf8;
          --ink: #163b3b;
          --ink2: #214f4d;
          --sage: #6f8f75;
          --sage2: #dbe6d7;
          --sage3: #edf4ec;
          --coral: #d9572e;
          --coral2: #bc4725;
          --plum: #72506f;
          --muted: #63706b;
          --line: rgba(22, 59, 59, 0.14);
          --shadow: 0 24px 58px rgba(30, 55, 50, 0.14);
          --soft: 0 14px 34px rgba(30, 55, 50, 0.10);
          --max: 1180px;

          overflow: hidden;
          background:
            radial-gradient(circle at 8% 0%, rgba(111, 143, 117, 0.20), transparent 30%),
            radial-gradient(circle at 92% 4%, rgba(217, 87, 46, 0.10), transparent 28%),
            linear-gradient(180deg, var(--cream2), var(--cream));
          color: var(--ink);
          font-family: "Greycliff CF", Inter, ui-sans-serif, system-ui, -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif;
          line-height: 1.55;
          -webkit-font-smoothing: antialiased;
        }

        .pdaa-shop-page *,
        .pdaa-shop-page *::before,
        .pdaa-shop-page *::after {
          box-sizing: border-box;
        }

        .pdaa-shop-page a {
          color: inherit;
          text-decoration: none;
        }

        .pdaa-shop-debug {
          padding: 12px 20px;
          background: #fff3cd;
          color: #5c4400;
          font-weight: 900;
          text-align: center;
          border-bottom: 1px solid rgba(92, 68, 0, 0.18);
          font-size: 14px;
        }

        .pdaa-shop-container {
          width: min(var(--max), calc(100% - 42px));
          margin-inline: auto;
        }

        .pdaa-shop-btn {
          display: inline-flex;
          align-items: center;
          justify-content: center;
          gap: 8px;
          min-height: 50px;
          padding: 15px 24px;
          border-radius: 999px;
          font-weight: 900;
          border: 1px solid transparent;
          transition: 0.2s ease;
          line-height: 1;
          cursor: pointer;
          white-space: nowrap;
        }

        .pdaa-shop-btn-coral {
          background: var(--coral);
          color: #ffffff !important;
          box-shadow: 0 14px 25px rgba(217, 87, 46, 0.20);
        }

        .pdaa-shop-btn-coral:hover,
        .pdaa-shop-btn-coral:focus-visible {
          background: var(--coral2);
          color: #ffffff !important;
          transform: translateY(-2px);
        }

        .pdaa-shop-btn-secondary {
          background: #ffffff;
          border-color: var(--line);
          color: var(--ink);
        }

        .pdaa-shop-btn-secondary:hover,
        .pdaa-shop-btn-secondary:focus-visible {
          transform: translateY(-2px);
          border-color: rgba(22, 59, 59, 0.30);
        }

        .pdaa-shop-eyebrow {
          display: inline-flex;
          align-items: center;
          gap: 8px;
          width: max-content;
          max-width: 100%;
          padding: 8px 14px;
          border-radius: 999px;
          background: rgba(111, 143, 117, 0.15);
          border: 1px solid rgba(111, 143, 117, 0.27);
          color: var(--ink2);
          font-weight: 950;
          letter-spacing: 0.06em;
          text-transform: uppercase;
          font-size: 12px;
          line-height: 1.2;
          margin-bottom: 22px;
        }

        .pdaa-shop-eyebrow::before {
          content: "";
          width: 7px;
          height: 7px;
          border-radius: 99px;
          background: var(--coral);
          flex: 0 0 auto;
        }

        .pdaa-shop-hero {
          position: relative;
          overflow: hidden;
          padding: 84px 0 58px;
        }

        .pdaa-shop-hero::after {
          content: "GUIDES";
          position: absolute;
          right: -80px;
          top: 10px;
          font-size: 180px;
          font-weight: 950;
          color: rgba(22, 59, 59, 0.035);
          line-height: 1;
          pointer-events: none;
        }

        .pdaa-shop-hero-grid {
          display: grid;
          grid-template-columns: 1.08fr 0.92fr;
          gap: 58px;
          align-items: center;
          position: relative;
          z-index: 1;
        }

        .pdaa-shop-title {
          color: var(--ink);
          font-size: clamp(42px, 5vw, 72px);
          line-height: 0.98;
          letter-spacing: -0.058em;
          margin: 0 0 22px;
          max-width: 820px;
          font-weight: 950;
        }

        .pdaa-shop-lead {
          color: #42524f;
          font-size: clamp(18px, 2vw, 22px);
          max-width: 760px;
          margin: 0 0 28px;
          line-height: 1.58;
        }

        .pdaa-shop-actions {
          display: flex;
          flex-wrap: wrap;
          gap: 14px;
        }

        .pdaa-shop-hero-panel {
          border-radius: 38px;
          background: linear-gradient(135deg, rgba(255, 255, 255, 0.98), rgba(232, 215, 189, 0.35));
          border: 1px solid rgba(22, 59, 59, 0.13);
          box-shadow: var(--shadow);
          padding: 34px;
        }

        .pdaa-shop-heading {
          color: var(--ink);
          font-size: clamp(30px, 3.2vw, 48px);
          line-height: 1.05;
          letter-spacing: -0.045em;
          margin: 0 0 18px;
          font-weight: 950;
        }

        .pdaa-shop-pathway-list {
          display: grid;
          gap: 14px;
          margin-top: 6px;
        }

        .pdaa-shop-pathway-item {
          display: grid;
          grid-template-columns: 44px 1fr;
          gap: 12px;
          align-items: start;
          padding: 16px;
          border-radius: 22px;
          background: #ffffff;
          border: 1px solid var(--line);
          box-shadow: 0 10px 24px rgba(30, 55, 50, 0.06);
          transition: 0.2s ease;
        }

        .pdaa-shop-pathway-item:hover,
        .pdaa-shop-pathway-item:focus-visible {
          transform: translateY(-2px);
          box-shadow: var(--soft);
        }

        .pdaa-shop-pathway-icon {
          width: 44px;
          height: 44px;
          display: grid;
          place-items: center;
          border-radius: 16px;
          background: var(--sage3);
          font-weight: 950;
          color: var(--ink);
        }

        .pdaa-shop-pathway-item strong {
          display: block;
          font-size: 16px;
          color: var(--ink);
        }

        .pdaa-shop-pathway-item span {
          color: var(--muted);
          font-size: 14px;
        }

        .pdaa-shop-section {
          padding: 54px 0;
        }

        .pdaa-shop-section-head {
          margin-bottom: 28px;
        }

        .pdaa-featured-grid {
          display: grid;
          grid-template-columns: 1fr 1fr;
          gap: 22px;
          margin-bottom: 42px;
        }

        .pdaa-products-grid {
          display: grid;
          grid-template-columns: repeat(3, 1fr);
          gap: 22px;
        }

        .pdaa-shop-product {
          background: var(--paper);
          border: 1px solid var(--line);
          border-radius: 34px;
          overflow: hidden;
          box-shadow: var(--soft);
          display: flex;
          flex-direction: column;
          min-height: 100%;
        }

        .pdaa-featured-product {
          display: grid;
          grid-template-columns: minmax(0, 0.82fr) minmax(0, 1.18fr);
          min-height: 320px;
        }

        .pdaa-featured-product > * {
          min-width: 0;
        }

        .pdaa-shop-cover {
          position: relative;
          overflow: hidden;
          min-height: 250px;
          display: flex;
          flex-direction: column;
          justify-content: space-between;
          padding: 24px;
          background: linear-gradient(145deg, var(--ink), #235350);
          color: #ffffff !important;
        }

        .theme-coral .pdaa-shop-cover {
          background: linear-gradient(145deg, #d9572e, #803a2d);
        }

        .theme-teal .pdaa-shop-cover {
          background: linear-gradient(145deg, #163b3b, #6f8f75);
        }

        .theme-plum .pdaa-shop-cover {
          background: linear-gradient(145deg, #72506f, #b7868c);
        }

        .theme-sage .pdaa-shop-cover {
          background: linear-gradient(145deg, #6f8f75, #214f4d);
        }

        .pdaa-shop-cover::after {
          content: "PDA";
          position: absolute;
          right: -18px;
          bottom: -18px;
          font-size: 98px;
          font-weight: 950;
          color: rgba(255, 255, 255, 0.10);
          line-height: 1;
        }

        .pdaa-shop-cover-image {
          background: linear-gradient(145deg, rgba(255,255,255,0.92), rgba(232,215,189,0.35)) !important;
          color: var(--ink) !important;
        }

        .pdaa-shop-cover-image::after {
          color: rgba(22, 59, 59, 0.06);
        }

        .pdaa-dynamic-image-wrap {
          position: relative;
          z-index: 1;
          display: grid;
          place-items: center;
          min-height: 245px;
          margin: 6px 0 12px;
        }

        .pdaa-dynamic-image-wrap img {
          display: block;
          width: auto;
          height: auto;
          max-width: 255px;
          max-height: 320px;
          object-fit: contain;
          border-radius: 0;
          box-shadow: none;
          background: transparent;
        }

        .pdaa-products-grid .pdaa-dynamic-image-wrap {
          height: 320px;
          min-height: 320px;
        }

        .pdaa-featured-product .pdaa-dynamic-image-wrap {
          width: 100%;
          min-width: 0;
          min-height: 270px;
        }

        .pdaa-featured-product .pdaa-dynamic-image-wrap img {
          /* Prevent portrait covers from overflowing the narrow featured column. */
          width: 100%;
          max-width: 270px;
          max-height: 340px;
        }

        .pdaa-cover-badge {
          position: relative;
          z-index: 1;
          width: max-content;
          max-width: 100%;
          padding: 8px 12px;
          border-radius: 999px;
          background: rgba(255, 255, 255, 0.16);
          border: 1px solid rgba(255, 255, 255, 0.24);
          font-size: 12px;
          font-weight: 950;
          text-transform: uppercase;
          letter-spacing: 0.06em;
          margin-bottom: 22px;
        }

        .pdaa-shop-cover-image .pdaa-cover-badge {
          color: var(--ink);
          background: rgba(111, 143, 117, 0.15);
          border-color: rgba(111, 143, 117, 0.27);
        }

        .pdaa-cover-short {
          position: relative;
          z-index: 1;
          font-size: 30px;
          line-height: 1.02;
          letter-spacing: -0.05em;
          font-weight: 950;
          max-width: 290px;
        }

        .pdaa-cover-footer {
          position: relative;
          z-index: 1;
          color: rgba(255, 255, 255, 0.78);
          font-weight: 850;
          font-size: 13px;
        }

        .pdaa-shop-cover-image .pdaa-cover-footer {
          color: var(--muted);
        }

        .pdaa-shop-product-body {
          padding: 24px;
          display: flex;
          flex-direction: column;
          flex: 1;
        }

        .pdaa-product-meta {
          display: flex;
          align-items: center;
          justify-content: space-between;
          gap: 12px;
          margin-bottom: 18px;
        }

        .pdaa-product-badge {
          color: var(--ink2);
          font-weight: 950;
          letter-spacing: 0.06em;
          text-transform: uppercase;
          font-size: 11px;
        }

        .pdaa-product-price {
          color: var(--coral);
          font-weight: 950;
          font-size: 17px;
          white-space: nowrap;
        }

        .pdaa-product-price del {
          color: var(--muted);
          margin-right: 7px;
          opacity: 0.72;
        }

        .pdaa-product-price ins {
          color: var(--coral);
          text-decoration: none;
        }

        .pdaa-shop-price-unavailable {
          color: var(--muted);
        }

        .pdaa-shop-product h3 {
          color: var(--ink);
          font-size: 24px;
          line-height: 1.12;
          letter-spacing: -0.03em;
          margin: 0 0 12px;
          font-weight: 950;
        }

        .pdaa-shop-product p {
          color: #42524f;
          margin: 0 0 16px;
          line-height: 1.55;
        }

        .pdaa-product-features {
          display: grid;
          gap: 8px;
          list-style: none;
          margin: 0 0 18px;
          padding: 0;
        }

        .pdaa-product-features li {
          display: flex;
          gap: 8px;
          color: #42524f;
          font-size: 14px;
          font-weight: 800;
        }

        .pdaa-product-features li::before {
          content: "✓";
          color: var(--coral);
          font-weight: 950;
        }

        .pdaa-product-footer {
          display: flex;
          align-items: center;
          justify-content: space-between;
          gap: 12px;
          margin-top: auto;
          padding-top: 16px;
          border-top: 1px solid rgba(22, 59, 59, 0.10);
        }

        .pdaa-product-footer span {
          color: var(--muted);
          font-size: 13px;
          font-weight: 800;
        }

        .pdaa-product-footer a {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  border-radius: 999px;
  background: var(--coral);
  color: #ffffff !important;
  font-weight: 950;
  padding: 11px 15px;
  font-size: 14px;
  box-shadow: 0 12px 22px rgba(217, 87, 46, 0.18);
  transition: transform 0.2s ease, background 0.2s ease, box-shadow 0.2s ease;
}

.pdaa-product-footer a:hover,
.pdaa-product-footer a:focus-visible {
  background: var(--coral2);
  color: #ffffff !important;
  transform: translateY(-2px);
  box-shadow: 0 16px 28px rgba(217, 87, 46, 0.24);
}

        .pdaa-filter-row {
          display: flex;
          flex-wrap: wrap;
          gap: 10px;
          margin-bottom: 26px;
        }

        .pdaa-filter-chip {
          padding: 10px 14px;
          border-radius: 999px;
          border: 1px solid var(--line);
          background: #ffffff;
          color: #42524f;
          font-weight: 850;
          font-size: 14px;
        }

        .pdaa-filter-chip.is-active {
          background: var(--ink);
          color: #ffffff;
        }

        .pdaa-trust-strip {
          display: grid;
          grid-template-columns: repeat(4, 1fr);
          gap: 14px;
          margin-top: 34px;
        }

        .pdaa-trust-item {
          border-radius: 24px;
          background: #ffffff;
          border: 1px solid var(--line);
          padding: 18px;
          box-shadow: 0 10px 24px rgba(30, 55, 50, 0.07);
        }

        .pdaa-trust-item strong {
          display: block;
          margin-bottom: 6px;
          color: var(--ink);
        }

        .pdaa-trust-item span {
          color: var(--muted);
          font-size: 14px;
        }

        @media (max-width: 1000px) {
          .pdaa-shop-hero-grid,
          .pdaa-featured-grid {
            grid-template-columns: 1fr;
          }

          .pdaa-products-grid {
            grid-template-columns: repeat(2, 1fr);
          }

          .pdaa-featured-product {
            grid-template-columns: 1fr;
          }

          .pdaa-trust-strip {
            grid-template-columns: repeat(2, 1fr);
          }
        }

        @media (max-width: 720px) {
          .pdaa-shop-container {
            width: min(100% - 28px, var(--max));
          }

          .pdaa-shop-hero {
            padding: 54px 0 34px;
          }

          .pdaa-shop-title {
            font-size: 42px;
          }

          .pdaa-products-grid,
          .pdaa-trust-strip {
            grid-template-columns: 1fr;
          }

          .pdaa-shop-hero-panel,
          .pdaa-shop-product {
            border-radius: 28px;
          }

          .pdaa-shop-cover {
            min-height: 210px;
          }

          .pdaa-dynamic-image-wrap {
            min-height: 230px;
          }

          .pdaa-dynamic-image-wrap img {
            max-width: 235px;
            max-height: 300px;
          }

          .pdaa-products-grid .pdaa-dynamic-image-wrap {
            height: 300px;
            min-height: 300px;
          }

          .pdaa-shop-btn {
            width: 100%;
          }
        }
      `}</style>

      {SHOW_WOOCOMMERCE_DEBUG ? <div className="pdaa-shop-debug">
          {debugMessage} Loaded product count: {loadedProductCount} /{" "}
          {allGuides.length}
        </div> : null}

      <section className="pdaa-shop-hero">
        <div className="pdaa-shop-container pdaa-shop-hero-grid">
          <div>
            <span className="pdaa-shop-eyebrow">Premium PDA Guide Library</span>

            <h1 className="pdaa-shop-title">
              Find the right PDA guide for home, school or NDIS planning
            </h1>

            <p className="pdaa-shop-lead">
              Practical Australian PDA and autism guides for parents, teachers and
              support teams. Start with the pathway that matches what feels hardest
              right now.
            </p>

            <div className="pdaa-shop-actions">
              <a className="pdaa-shop-btn pdaa-shop-btn-coral" href="#all-guides">
                Browse all guides
              </a>

              <a className="pdaa-shop-btn pdaa-shop-btn-secondary" href="/resources/">
                Start with free resources
              </a>
            </div>
          </div>

          <aside className="pdaa-shop-hero-panel">
            <span className="pdaa-shop-eyebrow">Choose by need</span>

            <h2 className="pdaa-shop-heading">What support do you need today?</h2>

            <div className="pdaa-shop-pathway-list">
              <a className="pdaa-shop-pathway-item" href="/product/funded-ndis-guide-pda/">
                <div className="pdaa-shop-pathway-icon">1</div>
                <div>
                  <strong>NDIS evidence &amp; funding</strong>
                  <span>Use FUNDED for templates, evidence and funding support.</span>
                </div>
              </a>

              <a className="pdaa-shop-pathway-item" href="/product/pda-teacher-handbook/">
                <div className="pdaa-shop-pathway-icon">2</div>
                <div>
                  <strong>School or teacher support</strong>
                  <span>Choose the Teacher Handbook for classroom strategies.</span>
                </div>
              </a>

              <a className="pdaa-shop-pathway-item" href="/product/complete-pda-bundle/">
                <div className="pdaa-shop-pathway-icon">3</div>
                <div>
                  <strong>Everything in one place</strong>
                  <span>
                    Choose the bundle for home, school, NDIS and emotional support.
                  </span>
                </div>
              </a>
            </div>
          </aside>
        </div>
      </section>

      <section className="pdaa-shop-section" id="all-guides">
        <div className="pdaa-shop-container">
          <div className="pdaa-shop-section-head">
            <span className="pdaa-shop-eyebrow">Featured guides</span>
            <h2 className="pdaa-shop-heading">Priority pathways</h2>
          </div>

          <div className="pdaa-featured-grid">
            {featuredGuides.map(guide => <ProductCard key={`featured-${guide.slug}`} guide={guide} product={products[guide.slug]} hasLoaded={hasLoaded} featured />)}
          </div>

          <div className="pdaa-shop-section-head">
            <span className="pdaa-shop-eyebrow">All products</span>
            <h2 className="pdaa-shop-heading">Full guide library</h2>
          </div>

          <div className="pdaa-filter-row" aria-label="Guide categories">
            <a className="pdaa-filter-chip is-active" href="#all-guides">
              All guides
            </a>
            <a className="pdaa-filter-chip" href="#parents">
              Parents
            </a>
            <a className="pdaa-filter-chip" href="#school">
              Teachers
            </a>
            <a className="pdaa-filter-chip" href="#ndis">
              NDIS
            </a>
            <a className="pdaa-filter-chip" href="#teens">
              Teens
            </a>
            <a className="pdaa-filter-chip" href="#rsd">
              Emotional support
            </a>
            <a className="pdaa-filter-chip" href="#bundle">
              Bundles
            </a>
          </div>

          <div className="pdaa-products-grid">
            {allGuides.map(guide => <ProductCard key={guide.slug} guide={guide} product={products[guide.slug]} hasLoaded={hasLoaded} />)}
          </div>

          <div className="pdaa-trust-strip">
            <div className="pdaa-trust-item">
              <strong>Instant digital access</strong>
              <span>Designed for downloadable PDF products.</span>
            </div>

            <div className="pdaa-trust-item">
              <strong>Australian context</strong>
              <span>Written for local families, schools and systems.</span>
            </div>

            <div className="pdaa-trust-item">
              <strong>Printable tools</strong>
              <span>Templates, scripts and practical worksheets.</span>
            </div>

            <div className="pdaa-trust-item">
              <strong>Neuro-affirming tone</strong>
              <span>Supportive, low-demand and practical.</span>
            </div>
          </div>
        </div>
      </section>
    </main>;
}
export default function ShopPage() {
  useEffect(() => {
    if (typeof document !== "undefined") {
      document.title = "PDA Guides & Autism Resources Australia | PDA Australia";
    }
  }, []);
  return <div>
      <Header />
      <PremiumShopContent />
      <Footer />
    </div>;
}